import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import { toast } from "react-toastify"
import axiosInstance from "../../../constants/axiosInstance"
import type {
  FetchNotificationsParams,
  Notification,
  NotificationFilterOptions,
  NotificationsPage,
  NotificationsResponse,
} from "../types"

interface MarkNotificationReadPayload {
  notificationId: string
  action: "read" | "unread"
}

interface MarkNotificationReadResponse {
  message?: string
}

const fetchNotifications = async ({
  pageParam,
  queryKey,
  limit,
}: FetchNotificationsParams & { limit: number }): Promise<NotificationsPage> => {
  const [, filters] = queryKey
  const payload = {
    ...filters,
    pageNumber: pageParam,
    limit,
  }
  const { data } = await axiosInstance().post<NotificationsResponse>("/notification/getNotifications", payload)
  const notificationsData = data.notifications

  return {
    readCount: notificationsData.readCount,
    unreadCount: notificationsData.unreadCount,
    allCount: notificationsData.allCount,
    totalPages: notificationsData.totalPages,
    notifications: notificationsData.allNotifications,
    currentPage: pageParam,
  }
}

const markNotificationApi = async (payload: MarkNotificationReadPayload): Promise<MarkNotificationReadResponse> => {
  const { data } = await axiosInstance().post<MarkNotificationReadResponse>("/notification/markNotification", payload)
  return data
}

const nextPageParam = (lastPage: NotificationsPage): number | undefined => {
  const { currentPage, totalPages } = lastPage
  if (currentPage < totalPages) {
    return currentPage + 1
  }
  return undefined
}

export const useFetchNotifications = (filters?: NotificationFilterOptions, limit = 10) => {
  const queryClient = useQueryClient()
  const queryKey = ["notifications", filters]

  const { data, error, isError, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, queryKey }) => fetchNotifications({ pageParam, queryKey, limit }),
    getNextPageParam: nextPageParam,
    initialPageParam: 1,
    retry: 3,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const markNotificationMutation = useMutation<
    MarkNotificationReadResponse,
    Error,
    MarkNotificationReadPayload,
    { previousData: any }
  >({
    mutationFn: markNotificationApi,

    onMutate: async ({ notificationId, action }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousData = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData?.pages) return oldData
        const isMarkingAsRead = action === "read"
        const newPages = oldData.pages.map((page: any) => {
          const itemIndex = page.notifications.findIndex((n: any) => n._id === notificationId)

          if (itemIndex === -1) return page

          const updatedPage = {
            ...page,
            readCount: page.readCount + (isMarkingAsRead ? 1 : -1),
            unreadCount: page.unreadCount - (isMarkingAsRead ? 1 : -1),
            notifications: page.notifications.map((notification: any) =>
              notification._id === notificationId ? { ...notification, status: action } : notification,
            ),
          }
          return updatedPage
        })

        return { ...oldData, pages: newPages }
      })

      return { previousData }
    },

    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      toast.error("Failed to update notification.")
    },
  })

  const notifications = useMemo<Notification[]>(() => {
    return data?.pages?.flatMap((page: NotificationsPage) => page.notifications || []) ?? []
  }, [data])

  const readCount = data?.pages[0]?.readCount ?? 0
  const unreadCount = data?.pages[0]?.unreadCount ?? 0
  const allCount = data?.pages[0]?.allCount ?? 0

  const markNotification = ({ notificationId, action }: { notificationId: string; action: 'read' | 'unread' }) => {
    return markNotificationMutation.mutate({ notificationId, action });
  }

  return {
    notifications,
    readCount,
    unreadCount,
    allCount,
    error,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    markNotification,
    isMarkingNotification: markNotificationMutation.isPending,
    markNotificationError: markNotificationMutation.error,
  }
}
